import {
  type ProcessorOptions,
  type Track,
  type TrackProcessor,
} from "livekit-client";
import { MuseTransformer } from "./transformers/types";
import { createTimerWorker, TimerWorker } from "./utils/timerHelper";

export default class ProcessorWrapper implements TrackProcessor<Track.Kind.Video> {
  name: string;

  source?: MediaStreamTrack;

  sourceSettings?: MediaTrackSettings;

  canvas?: HTMLCanvasElement;

  sourceDummy?: HTMLMediaElement;

  processedTrack?: MediaStreamTrack;

  transformer: MuseTransformer;
  timerWorker: TimerWorker | null = null;
  renderTimeoutId?: number;
  targetFps: number = 25;

  constructor({
    transformer,
    name,
    targetFps = 25,
  }: {
    transformer: MuseTransformer;
    name: string;
    targetFps?: number;
  }) {
    this.name = name;
    this.transformer = transformer;
    this.targetFps = Math.min(targetFps, 25);
  }

  private async setup(opts: ProcessorOptions<Track.Kind.Video>) {
    this.source = opts.track;
    this.sourceSettings = this.source.getSettings();
    this.sourceDummy = opts.element;
    if (this.sourceDummy instanceof HTMLVideoElement) {
      this.sourceDummy.height = this.sourceSettings.height ?? 300;
      this.sourceDummy.width = this.sourceSettings.width ?? 300;
    }
    if (!(this.sourceDummy instanceof HTMLVideoElement)) {
      throw TypeError("Currently only video transformers are supported");
    }

    this.canvas = document.createElement("canvas");
    this.canvas.width = this.sourceSettings.width ?? 300;
    this.canvas.height = this.sourceSettings.height ?? 300;
  }

  async init(opts: ProcessorOptions<Track.Kind.Video>) {
    await this.setup(opts);
    if (!this.canvas) {
      throw new TypeError("Expected canvas to be defined after setup");
    }
    if (!this.sourceDummy || !this.source) {
      throw new TypeError(
        "Expected sourceDummy and source to be defined after setup"
      );
    }

    const targetTimerTimeoutMs = 1000 / this.targetFps;
    this.timerWorker = createTimerWorker();

    const render = async () => {
      const startTime = performance.now();

      this.transformer.processFrame(
        this.sourceDummy as HTMLVideoElement,
        this.canvas!
      );
      this.renderTimeoutId = this.timerWorker!.setTimeout(
        render,
        Math.max(0, targetTimerTimeoutMs - (performance.now() - startTime))
      );
    };
    render();

    const track = this.canvas.captureStream();
    this.processedTrack = track.getTracks()[0];
  }

  async restart(opts: ProcessorOptions<Track.Kind.Video>) {
    await this.destroy();
    return this.init(opts);
  }

  async updateTransformerOptions({
    backgroundImage,
    blurFilterRadius,
  }: {
    backgroundImage?: string;
    blurFilterRadius?: number;
  }) {
    if (backgroundImage) {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.src = backgroundImage;
      await new Promise((resolve) => {
        image.onload = resolve;
      });
      this.transformer.update({ backgroundImage: image });
    }
    if (blurFilterRadius) {
      this.transformer.update({ blurFilterRadius });
    }
  }

  async destroy() {
    if (this.renderTimeoutId) {
      this.timerWorker?.clearTimeout(this.renderTimeoutId);
    }
    this.timerWorker?.terminate();
    this.transformer.destroy();
    this.processedTrack = undefined;
  }
}
